'use client';

import type { DashboardResponse } from '@/lib/dashboard/api';

/** Below this, `dashboard.pace_projection.confidence` reflects too little observed pace history to state plainly (issue #297). */
const LOW_CONFIDENCE_THRESHOLD = 0.4;

/**
 * Honest pace-projection card (issue #297): renders the server-computed
 * `pace_projection` from `GET /v1/dashboard` for both modes. Never computes
 * a projection client-side — the server's `confidence`/`projected_ready_date`/
 * `feasible` fields are the only source of truth. No countdown pressure, no
 * guilt copy: every branch states the current numbers plainly.
 */
export default function ProjectionCard({
  projection,
}: {
  projection: DashboardResponse['pace_projection'];
}) {
  if (projection.kind === 'no_deadline') {
    return (
      <section className="home-card" data-testid="projection-card">
        <h2 className="home-card-title">Projected ready date</h2>
        {projection.projected_ready_date === null ? (
          <p className="home-card-sub" data-testid="projection-withheld">
            Not enough history yet to estimate a ready date. Keep training and this will
            fill in.
          </p>
        ) : projection.confidence < LOW_CONFIDENCE_THRESHOLD ? (
          <>
            <p className="home-card-metric" data-testid="projection-provisional">
              Early estimate: ready around {projection.projected_ready_date}
            </p>
            <p className="home-card-sub">This will firm up as you train more.</p>
          </>
        ) : (
          <p className="home-card-metric" data-testid="projection-confident">
            Estimated ready around {projection.projected_ready_date}
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="home-card" data-testid="projection-card">
      <h2 className="home-card-title">Pace toward your exam date</h2>
      {projection.feasible ? (
        <>
          <p className="home-card-metric" data-testid="projection-feasible">
            {projection.required_atoms_per_day === 0
              ? `You're already on pace to be fully ready by ${projection.exam_date}.`
              : `About ${Math.ceil(projection.required_atoms_per_day ?? 0)} new things a day gets you there by ${projection.exam_date}.`}
          </p>
          {projection.confidence < LOW_CONFIDENCE_THRESHOLD && (
            <p className="home-card-sub" data-testid="projection-provisional">
              Early estimate — this will firm up as you train more.
            </p>
          )}
        </>
      ) : projection.reason === 'mastery_ceiling_too_low' ? (
        <p className="home-card-sub" data-testid="projection-infeasible">
          Even at full coverage of the current material, you won&apos;t reach full
          readiness by {projection.exam_date}. Consider moving the date, or accepting less
          than full readiness by then.
        </p>
      ) : (
        <p className="home-card-sub" data-testid="projection-infeasible">
          Reaching full readiness by {projection.exam_date} would take more new things a
          day than is realistic. Consider moving the date, or accepting less than full
          readiness by then.
        </p>
      )}
    </section>
  );
}
